import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import MonacoEditor from '@monaco-editor/react';
import axios from 'axios';
import GraphicsView from './GraphicsView';
import CanvasDisplay from './CanvasDisplay';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { lessons, exercises } from './learningData';
import { referenceSource, standardLibraryReferences } from './data/referenceData';
import { sanitizeVisibleText } from './utils/sanitizeVisibleText';

const API = '/c-learning/api';
const CONTENT_TYPE_OPTIONS = [
  { value: 'all', label: 'すべて' },
  { value: 'standard', label: '通常' },
  { value: 'graphics', label: 'グラフィックス' }
];

const graphicsReferenceItems = [
  {
    signature: 'void clear_canvas()',
    description: '描画領域を全て消去し、次のフレームを準備します。',
    exampleCode: 'clear_canvas();'
  },
  {
    signature: 'void set_color(int r, int g, int b)',
    description: 'RGB(0-255) 指定で描画色を設定します。',
    exampleCode: 'set_color(255, 0, 0);'
  },
  {
    signature: 'void draw_line(int x1, int y1, int x2, int y2)',
    description: '座標 (x1, y1) から (x2, y2) までの直線を描画します。',
    exampleCode: 'draw_line(20, 20, 180, 120);'
  },
  {
    signature: 'void draw_rect(int x, int y, int w, int h)',
    description: '左上 (x, y) から幅 w・高さ h の矩形を塗りつぶします。',
    exampleCode: 'draw_rect(50, 60, 120, 80);'
  },
  {
    signature: 'void draw_circle(int cx, int cy, int r)',
    description: '中心 (cx, cy)、半径 r の円を塗りつぶします。',
    exampleCode: 'draw_circle(200, 200, 40);'
  },
  {
    signature: 'void set_pixel(int x, int y)',
    description: '座標 (x, y) に現在の色で 1px を描画します。',
    exampleCode: 'set_pixel(100, 120);'
  },
  {
    signature: 'void update_frame()',
    description: 'ここまでの描画コマンドをクライアントに送信し、フレームを確定します。',
    exampleCode: 'update_frame();'
  },
  {
    signature: 'void wait_msec(int msec)',
    description: '指定したミリ秒だけ描画を一時停止します。',
    exampleCode: 'wait_msec(100);'
  },
  {
    signature: 'int get_key()',
    description: "キーボード入力を 1 文字取得します。(例: 'w', 'a' など)",
    exampleCode: "int key = get_key();\nif (key == 'w') {\n    draw_circle(200, 180, 20);\n}"
  }
];

const getContentType = (item) => (item.type === 'graphics' ? 'graphics' : 'standard');

const getReferenceItemCount = (library) =>
  library.sections.reduce((sum, section) => sum + section.items.length, 0);

const matchesSearch = (query, fields) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return fields
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(normalizedQuery);
};

const FilterPanel = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  onReset,
  resultLabel
}) => (
  <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">キーワード</label>
        <input
          type="text"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {filters.map((filter) => (
        <div key={filter.label}>
          <label className="block text-sm font-medium text-gray-700 mb-1">{filter.label}</label>
          <select
            value={filter.value}
            onChange={(event) => filter.onChange(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>

    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className="text-sm text-gray-600">{resultLabel}</p>
      <button
        onClick={onReset}
        className="self-start rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        フィルタをリセット
      </button>
    </div>
  </div>
);

// コード実行コンポーネント
const CodeRunner = ({ code, onCodeChange, resetCode, className = "" }) => {
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [inputData, setInputData] = useState('');
  const [showInput, setShowInput] = useState(false);

  const runCode = async () => {
    setIsRunning(true);
    setOutput('コンパイル中...');
    
    try {
      const response = await axios.post(`${API}/compile`, {
        code: code,
        input_data: inputData
      });
      
      const result = response.data;
      
      if (result.success) {
        let outputText = result.output;
        if (result.error && result.error.trim()) {
          outputText += `\n\n--- 警告/エラー情報 ---\n${sanitizeVisibleText(result.error)}`;
        }
        outputText += `\n\n--- 実行時間: ${result.execution_time.toFixed(3)}秒 ---`;
        setOutput(sanitizeVisibleText(outputText));
      } else {
        setOutput(`❌ ${sanitizeVisibleText(result.error)}`);
      }
    } catch (error) {
      console.error('コンパイルエラー:', error);
      setOutput(`❌ 通信エラー: ${sanitizeVisibleText(error.response?.data?.detail || error.message)}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      <div className="border-b p-4">
        <h2 className="text-xl font-semibold">コードエディタ</h2>
        <p className="text-sm text-gray-600 mt-1">下記のコードを編集して実行してみましょう</p>
      </div>
      
      <div className="p-4">
        <div className="border rounded-lg overflow-hidden mb-4">
          <MonacoEditor
            height="300px"
            language="c"
            theme="vs-light"
            value={code}
            onChange={(value) => onCodeChange(value || '')}
            options={{
              fontSize: 14,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true
            }}
          />
        </div>
        
        {/* 入力データセクション */}
        <div className="mb-4">
          <button
            onClick={() => setShowInput(!showInput)}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            {showInput ? '▼' : '▶'} 入力データ（scanf等を使用する場合）
          </button>
          {showInput && (
            <textarea
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              placeholder="プログラムへの入力データをここに入力してください..."
              className="w-full p-2 border rounded-lg text-sm"
              rows="3"
            />
          )}
        </div>
        
        <div className="flex gap-2 mb-4">
          <button
            onClick={runCode}
            disabled={isRunning}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
          >
            {isRunning ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                実行中...
              </>
            ) : (
              <>🚀 実行</>
            )}
          </button>
          <button
            onClick={resetCode}
            disabled={isRunning}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400"
          >
            🔄 リセット
          </button>
        </div>

        {/* 出力エリア */}
        {output && (
          <div className="p-4 bg-gray-900 text-green-400 rounded-lg font-mono text-sm">
            <h3 className="text-white mb-2 flex items-center gap-2">
              💻 実行結果:
            </h3>
            <pre className="whitespace-pre-wrap">{output}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

// レッスンコンポーネント
const LessonView = ({ lesson, onBack }) => {
  const [code, setCode] = useState(lesson.code);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-2"
          >
            ← レッスン一覧に戻る
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{lesson.title}</h1>
          <p className="text-gray-600 mt-1">{lesson.description}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* レッスン内容 */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-4">レッスン内容</h2>
            <div className="prose max-w-none">
              <div dangerouslySetInnerHTML={{ __html: lesson.content.replace(/\n/g, '<br>').replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>') }} />
            </div>
          </div>

          {/* コードエディタ */}
          <CodeRunner 
            code={code}
            onCodeChange={setCode}
            resetCode={() => setCode(lesson.code)}
          />
        </div>
      </div>
    </div>
  );
};

const buildExerciseContent = (exercise) => {
  const sections = [
    ['課題概要', exercise.overview],
    ['starterCode の見方', exercise.starterCodeGuide],
    [
      'TODO リスト',
      exercise.todoList && exercise.todoList.length > 0
        ? exercise.todoList.map((todo, index) => `${index + 1}. ${todo}`).join('\n')
        : ''
    ],
    [
      'やること',
      exercise.tasks.map((task, index) => `${index + 1}. ${task}`).join('\n')
    ],
    ['期待する動き', exercise.expectedBehavior],
    ['出力形式の注意', exercise.answerFormatNote],
    ['出力例', exercise.outputExample],
    [
      '正解の目安',
      exercise.successCriteria.map((criterion, index) => `${index + 1}. ${criterion}`).join('\n')
    ],
    [
      '確認ポイント',
      exercise.checkpoints.map((checkpoint, index) => `${index + 1}. ${checkpoint}`).join('\n')
    ]
  ].filter(([, body]) => body);

  return sections
    .map(([title, body]) => `${title}:\n${body}`)
    .join('\n\n');
};

// 演習コンポーネント
const ExerciseView = ({ exercise, onBack }) => {
  const [code, setCode] = useState(exercise.starterCode);
  const [showSolution, setShowSolution] = useState(false);

  if (exercise.type === 'graphics') {
    return (
      <GraphicsView
        lesson={{
          title: exercise.title,
          description: exercise.description,
          content: buildExerciseContent(exercise),
          code: exercise.starterCode
        }}
        onBack={onBack}
        backLabel="演習一覧に戻る"
        contentTitle="演習内容"
        resetLabel="スターターコードに戻す"
        autoRun={false}
        solutionCode={exercise.solution}
        solutionLabel="解答例"
        badges={[
          {
            label: exercise.category,
            className: 'rounded-full bg-blue-100 px-3 py-1 text-blue-800'
          },
          {
            label: exercise.difficulty,
            className: 'rounded-full bg-gray-100 px-3 py-1 text-gray-700'
          },
          {
            label: exercise.format,
            className: 'rounded-full bg-amber-100 px-3 py-1 text-amber-800'
          },
          {
            label: 'グラフィックス',
            className: 'rounded-full bg-emerald-100 px-3 py-1 text-emerald-800'
          }
        ]}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-2"
          >
            ← 演習一覧に戻る
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{exercise.title}</h1>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-800">
              {exercise.category}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">
              {exercise.difficulty}
            </span>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
              {exercise.format}
            </span>
            {exercise.type === 'graphics' && (
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                グラフィックス
              </span>
            )}
          </div>
          <p className="text-gray-600 mt-1">{exercise.description}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">課題概要</h2>
            <p className="text-sm leading-7 text-gray-700">{exercise.overview}</p>
          </div>
          {exercise.starterCodeGuide && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">starterCode の見方</h2>
              <p className="text-sm leading-7 text-gray-700">{exercise.starterCodeGuide}</p>
            </div>
          )}
          {exercise.todoList && exercise.todoList.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">TODO リスト</h2>
              <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-gray-700">
                {exercise.todoList.map((todo, index) => (
                  <li key={`${exercise.id}-todo-${index}`}>{todo}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">やること</h2>
            <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-gray-700">
              {exercise.tasks.map((task, index) => (
                <li key={`${exercise.id}-task-${index}`}>{task}</li>
              ))}
            </ul>
          </div>
          {exercise.expectedBehavior && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">期待する動き</h2>
              <p className="text-sm leading-7 text-gray-700">{exercise.expectedBehavior}</p>
            </div>
          )}
          {exercise.answerFormatNote && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">出力形式の注意</h2>
              <p className="text-sm leading-7 text-gray-700">{exercise.answerFormatNote}</p>
            </div>
          )}
          {exercise.outputExample && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">出力例</h2>
              <p className="text-sm leading-7 text-gray-700">{exercise.outputExample}</p>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">正解の目安</h2>
            <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-gray-700">
              {exercise.successCriteria.map((criterion, index) => (
                <li key={`${exercise.id}-criterion-${index}`}>{criterion}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">確認ポイント</h2>
            <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-gray-700">
              {exercise.checkpoints.map((checkpoint, index) => (
                <li key={`${exercise.id}-checkpoint-${index}`}>{checkpoint}</li>
              ))}
            </ul>
          </div>
        </div>

        <CodeRunner 
          code={code}
          onCodeChange={setCode}
          resetCode={() => setCode(exercise.starterCode)}
          className="mb-6"
        />
        
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <button 
            onClick={() => setShowSolution(!showSolution)}
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            {showSolution ? '解答を隠す' : '💡 解答を見る'}
          </button>

          {showSolution && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold mb-2">解答例:</h3>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                {exercise.solution}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>  
  );
};

// メインアプリコンポーネント
function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [lessonSearch, setLessonSearch] = useState('');
  const [lessonCategoryFilter, setLessonCategoryFilter] = useState('all');
  const [lessonDifficultyFilter, setLessonDifficultyFilter] = useState('all');
  const [lessonTypeFilter, setLessonTypeFilter] = useState('all');
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseCategoryFilter, setExerciseCategoryFilter] = useState('all');
  const [exerciseDifficultyFilter, setExerciseDifficultyFilter] = useState('all');
  const [exerciseTypeFilter, setExerciseTypeFilter] = useState('all');
  const [exerciseFormatFilter, setExerciseFormatFilter] = useState('all');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [referenceHeaderFilter, setReferenceHeaderFilter] = useState('all');
  const [referenceSectionFilter, setReferenceSectionFilter] = useState('all');

  const [freeCompileCode, setFreeCompileCode] = useState('#include <stdio.h>\n\nint main() {\n    printf("Hello, C Playground!\\n");\n    return 0;\n}');
  const [freeCompileInput, setFreeCompileInput] = useState('');
  const [freeCompileOutput, setFreeCompileOutput] = useState(null);

  const [isGraphicsMode, setIsGraphicsMode] = useState(false);
  const [freeGraphicsFrameCommands, setFreeGraphicsFrameCommands] = useState([]);
  const [freeGraphicsError, setFreeGraphicsError] = useState('');
  const [isGraphicsRunning, setIsGraphicsRunning] = useState(false);
  const [freeGraphicsInput, setFreeGraphicsInput] = useState('wwssda');
  const [freeGraphicsTextOutput, setFreeGraphicsTextOutput] = useState(''); 
  const commandQueueRef = useRef([]);

  const abortCtrlRef = useRef(null);
  const wsRef        = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      // 実行中だけ送る
      if ((isGraphicsRunning)
          && wsRef.current?.readyState === WebSocket.OPEN
      ) {
        wsRef.current.send(e.key);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isGraphicsRunning]);

  useEffect(() => {
    if (
      currentView === 'lesson' ||
      currentView === 'graphics' ||
      currentView === 'exercise'
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [currentView, selectedLesson?.title, selectedExercise?.id]);

  const openLesson = (lesson) => {
    setSelectedLesson(lesson);
    // レッスンのタイプをチェックして、表示するビューを切り替える
    if (lesson.type === 'graphics') {
      setCurrentView('graphics');
    } else {
      setCurrentView('lesson');
    }
  };

  const openExercise = (exercise) => {
    setSelectedExercise(exercise);
    setCurrentView('exercise');
  };

  const lessonCategories = ['all', ...new Set(lessons.map((lesson) => lesson.category))];
  const lessonDifficulties = ['all', ...new Set(lessons.map((lesson) => lesson.difficulty))];
  const exerciseCategories = ['all', ...new Set(exercises.map((exercise) => exercise.category || '発展'))];
  const exerciseDifficulties = ['all', ...new Set(exercises.map((exercise) => exercise.difficulty || '中級'))];
  const exerciseFormats = ['all', ...new Set(exercises.map((exercise) => exercise.format || '実装'))];
  const referenceHeaders = ['all', ...new Set(standardLibraryReferences.map((library) => library.header))];
  const referenceSectionTitles = [
    'all',
    ...new Set(standardLibraryReferences.flatMap((library) => library.sections.map((section) => section.title)))
  ];
  const totalReferenceItems = standardLibraryReferences.reduce(
    (sum, library) => sum + getReferenceItemCount(library),
    0
  );

  const filteredLessons = lessons.filter((lesson) => {
    const lessonType = getContentType(lesson);
    return (
      matchesSearch(lessonSearch, [lesson.title, lesson.description, lesson.category, lesson.difficulty]) &&
      (lessonCategoryFilter === 'all' || lesson.category === lessonCategoryFilter) &&
      (lessonDifficultyFilter === 'all' || lesson.difficulty === lessonDifficultyFilter) &&
      (lessonTypeFilter === 'all' || lessonType === lessonTypeFilter)
    );
  });

  const filteredExercises = exercises.filter((exercise) => {
    const exerciseType = getContentType(exercise);
    return (
      matchesSearch(exerciseSearch, [
        exercise.title,
        exercise.description,
        exercise.category,
        exercise.difficulty,
        exercise.format,
        exercise.overview,
        exercise.starterCodeGuide,
        exercise.expectedBehavior,
        exercise.answerFormatNote,
        exercise.outputExample,
        ...(exercise.todoList || []),
        ...(exercise.tasks || []),
        ...(exercise.successCriteria || []),
        ...(exercise.checkpoints || [])
      ]) &&
      (exerciseCategoryFilter === 'all' || exercise.category === exerciseCategoryFilter) &&
      (exerciseDifficultyFilter === 'all' || exercise.difficulty === exerciseDifficultyFilter) &&
      (exerciseTypeFilter === 'all' || exerciseType === exerciseTypeFilter) &&
      (exerciseFormatFilter === 'all' || exercise.format === exerciseFormatFilter)
    );
  });

  const isReferenceFilterActive =
    referenceSearch.trim() !== '' ||
    referenceHeaderFilter !== 'all' ||
    referenceSectionFilter !== 'all';

  const filteredReferences = standardLibraryReferences
    .filter((library) => referenceHeaderFilter === 'all' || library.header === referenceHeaderFilter)
    .map((library) => {
      const libraryMatchesSearch = matchesSearch(referenceSearch, [
        library.header,
        library.title,
        library.summary,
        library.note
      ]);

      const sections = library.sections
        .filter(
          (section) => referenceSectionFilter === 'all' || section.title === referenceSectionFilter
        )
        .map((section) => ({
          ...section,
          items:
            !referenceSearch.trim() || libraryMatchesSearch
              ? section.items
              : section.items.filter((item) =>
                  matchesSearch(referenceSearch, [
                    item.signature,
                    item.meaning,
                    item.usage,
                    item.exampleCode
                  ])
                )
        }))
        .filter((section) => section.items.length > 0);

      return sections.length > 0 ? { ...library, sections } : null;
    })
    .filter(Boolean);

  const filteredReferenceItemCount = filteredReferences.reduce(
    (sum, library) => sum + getReferenceItemCount(library),
    0
  );
  const filteredGraphicsReferences = graphicsReferenceItems.filter((item) =>
    !referenceSearch.trim() ||
    matchesSearch(referenceSearch, [item.signature, item.description, item.exampleCode])
  );
  const hasReferenceResults =
    filteredReferences.length > 0 ||
    filteredGraphicsReferences.length > 0;

  const stopFreeGraphics = () => {
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
      setIsGraphicsRunning(false);
      wsRef.current?.close();
      if (commandQueueRef.current.length) {
        setFreeGraphicsFrameCommands([...commandQueueRef.current]);
        commandQueueRef.current = [];
      }
    }
  };

  // App.js の 'handleFreeGraphicsExecute' 関数を以下に置き換える
  const handleFreeGraphicsExecute = () => {
    if (isGraphicsRunning) return;

    setIsGraphicsRunning(true);
    setFreeGraphicsError('');
    setFreeGraphicsTextOutput('');
    setFreeGraphicsFrameCommands([]);
    commandQueueRef.current = [];

    const ctrl = new AbortController();
    abortCtrlRef.current = ctrl;

    fetchEventSource(`${API}/stream-graphics`, {
        method: 'POST',
        signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            code: freeCompileCode,
            input_data: freeGraphicsInput
        }),

        onmessage(event) {
            const data = event.data;
            if (!data) return;

            try {
                const command = JSON.parse(data);
                if (command.event === 'error') {
                  setFreeGraphicsError(prev => prev + sanitizeVisibleText(command.data || '') + '\n');
                  return;
                }
                if (command.command === 'flush') {
                  setFreeGraphicsFrameCommands([...commandQueueRef.current]);
                  commandQueueRef.current = [];
                } else {
                  commandQueueRef.current.push(command);
                }
            } catch (e) {
                setFreeGraphicsTextOutput(prev => prev + sanitizeVisibleText(data) + '\n');
            }
        },

        onerror(err) {
             if (err.event?.data) {
                setFreeGraphicsError(prev => prev + sanitizeVisibleText(err.event.data) + '\n');
             }
            setIsGraphicsRunning(false);
            throw err;
        },
        onclose() {
            // サーバーがストリームを閉じたタイミングで必ず呼ばれる
            setIsGraphicsRunning(false);
             // flush イベントが来なかった最後のコマンドを反映
            if (commandQueueRef.current.length > 0) {
                setFreeGraphicsFrameCommands([...commandQueueRef.current]);
                commandQueueRef.current = [];
            }
        }
    });
  };


  if (currentView === 'exercise' && selectedExercise) {
    return <ExerciseView exercise={selectedExercise} onBack={() => setCurrentView('home')} />;
  }

  if (currentView === 'graphics' && selectedLesson) {
    return <GraphicsView lesson={selectedLesson} onBack={() => setCurrentView('home')} />;
  }

  if (currentView === 'lesson' && selectedLesson) {
    return <LessonView lesson={selectedLesson} onBack={() => setCurrentView('home')} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">C言語学習サイト</h1>
              <p className="text-gray-600 mt-1">実践的なC言語プログラミングを学びましょう</p>
            </div>
            <div className="bg-green-100 px-4 py-2 rounded-lg">
              <span className="text-green-800 font-semibold">✅ 実際のCコンパイル対応</span>
            </div>
          </div>
        </div>
      </header>

      {/* ナビゲーション */}
      <nav className="bg-blue-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setCurrentView('home')}
              className={`py-4 px-2 text-white hover:text-blue-200 border-b-2 ${currentView === 'home' ? 'border-white' : 'border-transparent'}`}
            >
              レッスン
            </button>
            <button
              onClick={() => setCurrentView('exercises')}
              className={`py-4 px-2 text-white hover:text-blue-200 border-b-2 ${currentView === 'exercises' ? 'border-white' : 'border-transparent'}`}
            >
              演習問題
            </button>
            <button
              onClick={() => setCurrentView('free-compile')}
              className={`py-4 px-2 text-white hover:text-blue-200 border-b-2 ${currentView === 'free-compile' ? 'border-white' : 'border-transparent'}`}
            >
              フリーコンパイル
            </button>
            <button
              onClick={() => setCurrentView('reference')}
              className={`py-4 px-2 text-white hover:text-blue-200 border-b-2 ${currentView === 'reference' ? 'border-white' : 'border-transparent'}`}
            >
              リファレンス
            </button>
          </div>
        </div>
      </nav>

      {/* メインコンテンツ */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {currentView === 'home' && (
          <div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="text-green-800 font-semibold mb-2">🎉 新機能リリース！</h3>
              <p className="text-green-700 text-sm">
                実際のGCCコンパイラを使用してCコードをコンパイル・実行できるようになりました！エラー表示、実行時間測定、入力データ対応など、本格的な開発環境を体験できます。
              </p>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">レッスン一覧</h2>
            <FilterPanel
              searchValue={lessonSearch}
              onSearchChange={setLessonSearch}
              searchPlaceholder="タイトル・説明・カテゴリで検索"
              filters={[
                {
                  label: 'カテゴリ',
                  value: lessonCategoryFilter,
                  onChange: setLessonCategoryFilter,
                  options: lessonCategories.map((category) => ({
                    value: category,
                    label: category === 'all' ? 'すべて' : category
                  }))
                },
                {
                  label: '難易度',
                  value: lessonDifficultyFilter,
                  onChange: setLessonDifficultyFilter,
                  options: lessonDifficulties.map((difficulty) => ({
                    value: difficulty,
                    label: difficulty === 'all' ? 'すべて' : difficulty
                  }))
                },
                {
                  label: '種別',
                  value: lessonTypeFilter,
                  onChange: setLessonTypeFilter,
                  options: CONTENT_TYPE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label
                  }))
                }
              ]}
              onReset={() => {
                setLessonSearch('');
                setLessonCategoryFilter('all');
                setLessonDifficultyFilter('all');
                setLessonTypeFilter('all');
              }}
              resultLabel={`${filteredLessons.length} / ${lessons.length} 件のレッスンを表示中`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openLesson(lesson)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {lesson.category}
                      </span>
                      <span className="text-gray-500 text-sm">{lesson.difficulty}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {lesson.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {lesson.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {filteredLessons.length === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-gray-500">
                条件に一致するレッスンがありません。
              </div>
            )}
          </div>
        )}

        {currentView === 'exercises' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">演習問題</h2>
            <FilterPanel
              searchValue={exerciseSearch}
              onSearchChange={setExerciseSearch}
              searchPlaceholder="タイトル・説明・問題形式・確認ポイントで検索"
              filters={[
                {
                  label: 'カテゴリ',
                  value: exerciseCategoryFilter,
                  onChange: setExerciseCategoryFilter,
                  options: exerciseCategories.map((category) => ({
                    value: category,
                    label: category === 'all' ? 'すべて' : category
                  }))
                },
                {
                  label: '難易度',
                  value: exerciseDifficultyFilter,
                  onChange: setExerciseDifficultyFilter,
                  options: exerciseDifficulties.map((difficulty) => ({
                    value: difficulty,
                    label: difficulty === 'all' ? 'すべて' : difficulty
                  }))
                },
                {
                  label: '内容',
                  value: exerciseTypeFilter,
                  onChange: setExerciseTypeFilter,
                  options: CONTENT_TYPE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label
                  }))
                },
                {
                  label: '問題形式',
                  value: exerciseFormatFilter,
                  onChange: setExerciseFormatFilter,
                  options: exerciseFormats.map((format) => ({
                    value: format,
                    label: format === 'all' ? 'すべて' : format
                  }))
                }
              ]}
              onReset={() => {
                setExerciseSearch('');
                setExerciseCategoryFilter('all');
                setExerciseDifficultyFilter('all');
                setExerciseTypeFilter('all');
                setExerciseFormatFilter('all');
              }}
              resultLabel={`${filteredExercises.length} / ${exercises.length} 件の演習を表示中`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredExercises.map((exercise) => (
                <div
                  key={exercise.id}
                  className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openExercise(exercise)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                          {exercise.category}
                        </span>
                        <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-semibold">
                          {exercise.format}
                        </span>
                        {exercise.type === 'graphics' && (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                            グラフィックス
                          </span>
                        )}
                      </div>
                      <span className="text-gray-500 text-sm">{exercise.difficulty}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {exercise.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {exercise.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {filteredExercises.length === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-gray-500">
                条件に一致する演習問題がありません。
              </div>
            )}
          </div>
        )}

        {currentView === 'free-compile' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">フリーコンパイル</h2>
                <p className="text-gray-600 mt-1">C言語のコードを自由に入力・実行できます。</p>
              </div>
              {/* --- グラフィックスモード切替スイッチ --- */}
              <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                <span className="text-sm font-medium text-gray-700">通常モード</span>
                <label htmlFor="graphics-toggle" className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" id="graphics-toggle" className="sr-only peer" checked={isGraphicsMode} onChange={() => setIsGraphicsMode(!isGraphicsMode)} />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className="text-sm font-medium text-blue-600">グラフィックスモード</span>
              </div>
            </div>

            {/* --- モードに応じて表示を切り替え --- */}
            {!isGraphicsMode ? (
              // 通常モード
              <CodeRunner
                code={freeCompileCode}
                onCodeChange={setFreeCompileCode}
                resetCode={() => setFreeCompileCode('#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}')}
              />
            ) : (
              // グラフィックスモード
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 左側：エディタと入力 */}
                <div>
                  <div className="border rounded-lg overflow-hidden mb-4 bg-gray-900">
                    <MonacoEditor
                      height="350px"
                      language="c"
                      theme="vs-dark"
                      value={freeCompileCode}
                      onChange={(value) => setFreeCompileCode(value || '')}
                      options={{ fontSize: 14, minimap: { enabled: false } }}
                    />
                  </div>

                  {/* ★★★ ここからが追加部分 ★★★ */}
                  <div className="mb-4">
                    <label htmlFor="graphics-keyboard-input" className="block text-sm font-medium text-gray-700">
                      キーボード入力シーケンス
                    </label>
                    <textarea
                      id="graphics-keyboard-input"
                      rows="2"
                      className="w-full p-2 mt-1 border rounded-md font-mono"
                      value={freeGraphicsInput}
                      onChange={(e) => setFreeGraphicsInput(e.target.value)}
                      placeholder="ここに入力した文字がget_key()で順番に読み込まれます (例: wwssad)"
                    />
                  </div>
                  {/* ★★★ ここまでが追加部分 ★★★ */}

                  <div className="flex gap-2">
                    <button onClick={handleFreeGraphicsExecute} disabled={isGraphicsRunning} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400">
                      {isGraphicsRunning ? '実行中...' : '🚀 実行'}
                    </button>
                    <button
                      onClick={stopFreeGraphics}
                      disabled={!isGraphicsRunning}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                    >⏹️ 停止</button>
                    <button onClick={() => setFreeCompileCode(lessons.find(l => l.type === 'graphics').code)} disabled={isGraphicsRunning} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:bg-gray-400">
                      🔄 サンプルにリセット
                    </button>
                  </div>
                </div>
                {/* 右側：キャンバスと出力 */}
                <div>
                <CanvasDisplay commands={freeGraphicsFrameCommands} />
                  
                  {freeGraphicsTextOutput && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">テキスト出力 (printf)</h3>
                      <pre className="p-3 bg-gray-900 text-white rounded-lg font-mono text-xs whitespace-pre-wrap h-24 overflow-y-auto">
                        {freeGraphicsTextOutput}
                      </pre>
                    </div>
                  )}
                  {freeGraphicsError && (
                    <div className="mt-4 p-4 bg-red-900 text-red-300 rounded-lg font-mono text-sm">
                        <h3 className="font-semibold mb-2">エラー:</h3>
                        <pre className="whitespace-pre-wrap">{freeGraphicsError}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'reference' && (
          <div>
            <FilterPanel
              searchValue={referenceSearch}
              onSearchChange={setReferenceSearch}
              searchPlaceholder="関数名、マクロ名、説明、使用例コードで検索"
              filters={[
                {
                  label: 'ヘッダ',
                  value: referenceHeaderFilter,
                  onChange: setReferenceHeaderFilter,
                  options: referenceHeaders.map((header) => ({
                    value: header,
                    label: header === 'all' ? 'すべて' : header
                  }))
                },
                {
                  label: '項目種別',
                  value: referenceSectionFilter,
                  onChange: setReferenceSectionFilter,
                  options: referenceSectionTitles.map((title) => ({
                    value: title,
                    label: title === 'all' ? 'すべて' : title
                  }))
                }
              ]}
              onReset={() => {
                setReferenceSearch('');
                setReferenceHeaderFilter('all');
                setReferenceSectionFilter('all');
              }}
              resultLabel={`${filteredReferenceItemCount} / ${totalReferenceItems} 標準項目, ${filteredGraphicsReferences.length} / ${graphicsReferenceItems.length} グラフィックス API を表示中`}
            />
            <div className="mb-8 p-4 bg-white rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold mb-2">目次</h3>
              <ul className="list-disc list-inside text-sm text-blue-600">
                <li><a href="#toc-standard-library">C標準ライブラリ一覧</a></li>
                <li><a href="#toc-graphics-api">独自グラフィック API 一覧</a></li>
                <li><a href="#toc-coordinates">グラフィック座標系のルール</a></li>
              </ul>
            </div>
            <div id="toc-standard-library" className="bg-white rounded-lg shadow-sm border p-6 mb-8">
              <h3 className="text-lg font-semibold mb-2">C標準ライブラリ一覧</h3>
              <p className="text-sm text-gray-600 mb-6">
                この一覧では、WG14 の C11 ドラフトにある Library summary をもとに、標準ヘッダごとの定義・型・関数を整理しています。
                実装依存の有無はありますが、学習用リファレンスとして規格上の全体像を追える構成にしています。
              </p>
              <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                <p className="font-medium">{referenceSource.title}</p>
                <p>{referenceSource.scope}</p>
                <a
                  href={referenceSource.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-2 text-blue-700 underline"
                >
                  公式資料を見る
                </a>
              </div>
              <div className="space-y-6">
                {filteredReferences.map((library) => (
                  <details
                    key={library.header}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                    open={isReferenceFilterActive ? true : undefined}
                  >
                    <summary className="cursor-pointer list-none">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-blue-700">{library.header}</h4>
                            <span className="text-xs text-gray-400">{library.standard}</span>
                            <span className="rounded-full bg-white px-2 py-1 text-[11px] text-gray-500 border">
                              {getReferenceItemCount(library)} 項目
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-700">{library.summary}</p>
                          {library.note && (
                            <p className="mt-1 text-xs text-amber-700">{library.note}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">クリックで展開</span>
                      </div>
                    </summary>

                    <div className="mt-4 space-y-4">
                      {library.sections.map((section) => (
                        <div key={`${library.header}-${section.title}`}>
                          <h5 className="mb-2 text-sm font-semibold text-gray-800">
                            {section.title}
                          </h5>
                          <ul className="space-y-1 text-xs text-gray-600">
                            {section.items.map((item) => (
                              <li
                                key={`${library.header}-${section.title}-${item.signature}`}
                                className="rounded bg-white px-3 py-2 border border-gray-100"
                              >
                                <code className="whitespace-pre-wrap break-all">{item.signature}</code>
                                <p className="mt-2 text-[11px] leading-relaxed text-gray-700">
                                  <span className="font-semibold text-gray-800">意味:</span> {item.meaning}
                                </p>
                                <p className="mt-1 text-[11px] leading-relaxed text-gray-600">
                                  <span className="font-semibold text-gray-800">使い方:</span> {item.usage}
                                </p>
                                {item.exampleCode && (
                                  <div className="mt-2">
                                    <p className="text-[11px] font-semibold text-gray-800">使用例:</p>
                                    <pre className="mt-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 text-[11px] text-green-300">
                                      <code>{item.exampleCode}</code>
                                    </pre>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </div>
            {!hasReferenceResults && (
              <div className="mb-8 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                条件に一致するリファレンス項目がありません。
              </div>
            )}

            <div id="toc-graphics-api" className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">独自グラフィック API 一覧</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredGraphicsReferences.map((item) => (
                  <div key={item.signature} className="border rounded-lg p-4 bg-white">
                    <h4 className="font-semibold text-green-600">{item.signature}</h4>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold text-gray-800">使用例:</p>
                      <pre className="mt-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 text-[11px] text-green-300">
                        <code>{item.exampleCode}</code>
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
              {filteredGraphicsReferences.length === 0 && (
                <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  グラフィックス API に一致する項目がありません。
                </div>
              )}
            </div>
            <div id="toc-coordinates" className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">グラフィック座標系のルール</h3>
              <p className="text-sm text-gray-600 mb-4">
                このキャンバスはピクセル単位の 2D 座標系を使っています。  
                原点 (0, 0) は左上隅にあり、  
                → X 軸は右方向に、  
                ↓ Y 軸は下方向に値が大きくなります。
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                <li><strong>X 軸</strong>: 左(0) → 右(最大 400)</li>
                <li><strong>Y 軸</strong>: 上(0) → 下(最大 400)</li>
                <li><strong>単位</strong>: ピクセル</li>
                <li><strong>キャンバスサイズ</strong>: 横 400 px × 縦 400 px固定</li>
              </ul>
              <p className="text-sm text-gray-600">
                例えば<br/>
                <code>draw_circle(200, 200, 50);</code> を呼ぶと、  
                キャンバスの中央 (200, 200) を中心に半径 50 px の円が描画されます。
              </p>
            </div>

          </div>          
        )}
      </main>
    </div>
  );
}


export default App;
